import { useEffect, useState } from "react";
import { BsFilterRight } from "react-icons/bs";
import "./video.scss";
import PlansAndProducts from "../../components/plansAndProducts/PlansAndProducts";
import VideoPlayer from "../../components/player/VideoPlayer";
import "./paginate.scss";
import useAuth from "../../hooks/useAuth";
import {
  useGetFeaturedVideoQuery,
  useGetFilteredVideosQuery,
} from "../api/videoApiSlice";
import LoadingSpinner from "../../components/loader/Loader";
import { useVideo } from "../../hooks/useVideo";
import FilterComponent from "./FilterVideo";
import PaginatedVideosGrid from "./PaginatedVideosGrid";
import { useNavigate, useSearchParams } from "react-router-dom";

const VideosList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromQuery = parseInt(searchParams.get("page") || "1", 10);
  const [currentPage, setCurrentPage] = useState(pageFromQuery);
  const [mobileFilter, setMobileFilter] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const { scrollToTop, handleSelectedVideoToPlay, selectedVideo } = useVideo();

  const { data: featuredVideo, isLoading: featuredLoading } =
    useGetFeaturedVideoQuery();
  const videoType = searchParams.get("videoType") || "";
  const category = searchParams.get("category") || "";
  const searchTerm = searchParams.get("searchTerm") || "";
  const videosPerPage = 9;
  const { isLoggedIn } = useAuth();

  // New handler for filter changes and pagination
  const handleFilterChange = (newFilterState) => {
    // Keep existing params but add/update new filter state and reset page to 1
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams({
      ...currentParams,
      ...newFilterState,
      page: newFilterState.page || "1", // Ensure page is reset or kept
    });
  };

  // ✅ Create the query parameters object directly from search params
  const queryParams = {
    videoType,
    searchTerm,
    category,
    page: pageFromQuery,
    limit: videosPerPage,
  };

  // ✅ Pass the entire object to the query hook
  const { data, isLoading, refetch } = useGetFilteredVideosQuery(queryParams);

  const {
    videos = [],
    totalPages = 1,
    totalVideos = 0,
    isSubscribed = false,
    trialActive = false,
    hasElevatedAccess,
  } = data || {};

  // Refetch videos when isLoggedIn changes, so you always get the correct subscription state immediately
  useEffect(() => {
    if (isLoggedIn) {
      refetch();
    }
  }, [isLoggedIn, refetch]);

  // keep in sync when query changes
  useEffect(() => {
    if (pageFromQuery !== currentPage) {
      setCurrentPage(pageFromQuery);
    }
  }, [pageFromQuery]);

  // This sets featured as the first video in video player
  useEffect(() => {
    if (!selectedVideo) {
      if (featuredVideo?._id) {
        handleSelectedVideoToPlay({ ...featuredVideo, isFeatured: true });
        scrollToTop();
      } else if (videos?.length > 0) {
        handleSelectedVideoToPlay(videos[0]);
        scrollToTop();
      }
    }
  }, [videos, featuredVideo, selectedVideo]);

  // This sets video [0] as the first video in video player
  // useEffect(() => {
  //   if (videos?.length > 0) {
  //     handleSelectedVideoToPlay(videos[0]);
  //     scrollToTop();
  //   }
  // }, [videos]);

  useEffect(() => {
    if (selectedVideo?._id) {
      setActiveVideoId(selectedVideo._id);
      scrollToTop();
    }
  }, [selectedVideo]);

  useEffect(() => {
    setCurrentPage(1);
  }, [videoType, searchTerm, category]);

  const toggleMobileFilterMenu = () => setMobileFilter((prev) => !prev);

  if (isLoading || featuredLoading) return <LoadingSpinner />;

  return (
    <div className="main-area-flex">
      <div className="main-area-left">
        <FilterComponent
          videoType={videoType}
          category={category}
          searchTerm={searchTerm}
          handleFilterChange={handleFilterChange}
          mobileFilter={mobileFilter}
          toggleMobileFilterMenu={toggleMobileFilterMenu}
          videos={videos}
        />
      </div>
      {mobileFilter && (
        <div
          className="mobile-filter-overlay active"
          onClick={toggleMobileFilterMenu}
        ></div>
      )}
      {!mobileFilter && (
        <button
          className="floating-filter-btn"
          onClick={toggleMobileFilterMenu}
        >
          <BsFilterRight aria-hidden="true" />
          Filters
        </button>
      )}

      <div className="main-area-center">
        <VideoPlayer
          videos={videos}
          isSubscribed={isSubscribed}
          hasElevatedAccess={hasElevatedAccess}
          trialActive={trialActive}
          video={featuredVideo}
        />

        <div className="video-area">
          <div className="video-header">
            <h4 className="total-videos">Total Videos: {totalVideos}</h4>
          </div>

          <div className="related-videos">
            <PaginatedVideosGrid
              videos={videos}
              currentPage={pageFromQuery}
              totalPages={totalPages}
              // ✅ Pass the setSearchParams directly or a handler
              handlePageClick={(newPage) =>
                handleFilterChange({ page: newPage })
              }
              activeVideoId={selectedVideo?._id}
              source="videoList"
              navigate={navigate}
            />
          </div>
        </div>
      </div>
      <PlansAndProducts videos={videos} />
    </div>
  );
};

export default VideosList;
